/**
 * React browser bootstrap — the entry point for the browser code in the Web App.
 * @module browser
 */
import React from 'react'
import { hydrateRoot } from 'react-dom/client'
import _get from 'lodash/get'
import _isDate from 'lodash/isDate'
import type { History } from 'history'
import { get as getCookie } from 'cookies-js'
import { getAppData } from './react-universal/browser'
import { get as getConfig } from './config/index'
import { setLogger } from './log/index'
import { getLogger as getLoggerBrowser } from './log/browser'
import { createStore } from './store/configureStore'
import { initialState } from './reducers/index'
import { AUTH_COOKIE_NAME, setTokenDataInStorage } from './services/auth/index'
import { createHistory, parseLocation } from './services/resolver/index'
import { isLogout } from './services/url/index'
import attachResolverHistoryListener from './browser/attach-resolver-history-listener'
import { preloadHydrateRouteModules } from './browser/preloadHydrateRouteModules'
import App from './components/App/index'
import type { AppStore } from './components/App/index'
import { getSessionStorage, SESSION_STORAGE_GAIA_PLAN_SELECTION, SESSION_STORAGE_GAIA_CHECKOUT_DISCOUNT_CODE } from './services/local-preferences/index'
import { getAnonymousUuid } from './browser/anonymous/index'
import { init as initNewRelic } from './browser/new-relic/index'
import { isLocal } from './environment/index'
import { markMilestone } from './new-relic-transaction/index'
import { client as apolloClient } from './services/apollo-graphql/index'
import { restoreApolloContentSeeds } from './services/content/restoreApolloContentSeeds'

/**
 * The `app` slice as the server seeds it. The render flags are part of the SSR
 * contract: the server decides them and the first client render must reuse the
 * same values, so bootstrap only writes the fields that are genuinely
 * client-side (client/server, bootstrap progress, viewport).
 */
interface BootstrapAppState {
  isWebView?: boolean
  server?: boolean
  client?: boolean
  scrollable?: boolean
  bootstrapPhase?: string | null
  bootstrapComplete?: boolean
  initializationStartTime?: number
  renderHeader?: boolean
  renderFooter?: boolean
  renderApp?: boolean
  viewport?: unknown
}

interface BootstrapAuthState {
  jwt?: string
  uid?: number
}

interface BootstrapUserState {
  data?: Record<string, unknown>
}

interface BootstrapResolverState {
  location?: unknown
  path?: string
  query?: unknown
  processing?: boolean
}

/**
 * The slice of the serialized hydrate payload this module reads or writes. The
 * server seeds `app` and `resolver` on every render, so they are not optional.
 */
type BootstrapHydrate = {
  app: BootstrapAppState
  resolver: BootstrapResolverState
  auth?: BootstrapAuthState
  user?: BootstrapUserState
  plans?: { selection?: unknown }
  checkout?: { discountCode?: unknown }
}

type BootstrapAppData = {
  hydrate?: BootstrapHydrate
}

/** The browser logger installed by `initLogger`, which accepts an error + message. */
interface BootstrapLogger {
  error: (error: unknown, message?: string) => unknown
}

interface RenderAppOptions {
  store: AppStore
  history: History
}

const config = getConfig()
const ENV_LOCAL = isLocal()

function isRecord (value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

/**
 * `getAppData()` is declared with only the fields its other consumers read, so
 * the payload is re-read here and narrowed to the wider slice bootstrap owns.
 */
function getBootstrapAppData (): BootstrapAppData {
  const appData: unknown = getAppData()
  return isRecord(appData) ? (appData as BootstrapAppData) : {}
}

/** The ambient `history` types expose `location` as `unknown`. */
function getHistoryLocation (history: History): Record<string, unknown> | undefined {
  const { location } = history
  return isRecord(location) ? location : undefined
}

/**
 * Reload the page when the data is not syned between
 * the hydrated data and the cookies to prevent inconsitent
 * behavior in the application
 */
function reloadNotSynced (logger: BootstrapLogger) {
  // Don't block the logout page.
  if (isLogout(
    _get(window, ['location', 'pathname']),
  )) {
    return
  }

  try {
    const appData = getBootstrapAppData()
    const authCookie = getCookie(AUTH_COOKIE_NAME)
    const auth: unknown = authCookie ? JSON.parse(authCookie) : {}
    const appAuth = appData.hydrate?.auth
    const appJwt = appAuth?.jwt
    const cookieJwt = isRecord(auth) ? auth.jwt : undefined
    if (cookieJwt !== appJwt) {
      // Write the app auth to the cookies which will also clean duplicates.
      // A missing auth expires the cookie rather than rewriting it.
      setTokenDataInStorage(appAuth)
      // Prevent reload in local environment due to dev tools causing infinite reloads
      if (ENV_LOCAL) {
        return
      }
      const lastAuthWindowReloadDateStoreValue = sessionStorage.getItem('auth-window-reload-date')
      let shouldReload = false
      if (!lastAuthWindowReloadDateStoreValue) {
        shouldReload = true
      } else {
        const lastAuthWindowReloadDate = new Date(lastAuthWindowReloadDateStoreValue)
        if (_isDate(lastAuthWindowReloadDate)) {
          const now = new Date()
          const diff = now.getTime() - lastAuthWindowReloadDate.getTime()
          if (diff >= 15 * 60 * 1000) { // 15 minutes in milliseconds
            shouldReload = true
          }
        } else {
          shouldReload = true
        }
      }
      if (shouldReload) {
        sessionStorage.setItem('auth-window-reload-date', new Date().toISOString())
        window.location.reload()
      }
    }
  } catch (err) {
    logger.error(err, 'Failed parse auth cookie data in reloadNotSynced')
  }
}

/**
 * Initialize a buyan logger and set it in the library
 * as a singleton
 */
function initLogger (): BootstrapLogger {
  const appData = getBootstrapAppData()
  const uid = appData.hydrate?.auth?.uid
  const logger: BootstrapLogger = getLoggerBrowser(uid)
  setLogger(logger)
  return logger
}

function renderApp (options: RenderAppOptions) {
  const { store, history } = options
  return (
    <App
      store={store}
      history={history}
    />
  )
}

/**
 * The entry point for the browser code in the Web App.
 */
export async function run () {
  const {
    log: {
      browser: {
        level: logLevel,
      } = {},
    } = {},
  } = config
  const startTime = new Date().getTime()
  const logger = initLogger()

  function logBrowserError (error: unknown, name: string) {
    logger.error(error, `Browser run failed for ${name}`)
  }

  try {
    window.addEventListener('unhandledrejection', (e) => {
      e.preventDefault()
      const reason = _get(e, ['reason'])
      const detailReason = _get(e, ['detail', 'reason'])
      const log = logger || console
      log.error(detailReason || reason || 'Unknown reason in unhandledrejection')
    })

    initNewRelic({ appData: getAppData() })
    reloadNotSynced(logger)

    const appData = getBootstrapAppData()
    // The url argument only matters on the server, where history is created in
    // memory; in the browser `createHistory` reads the live location itself.
    const history: History = createHistory(window.location.href)
    const { hydrate } = appData
    if (!hydrate) {
      throw new Error('Bootstrap requires a server hydrated app data payload')
    }
    const rawAuth = hydrate.auth
    const historyLocation = parseLocation(getHistoryLocation(history))
    // The server seeds `hydrate.user`, but bootstrap writes into its data bag
    // unconditionally, so the bag is materialized rather than assumed.
    const userData: Record<string, unknown> = hydrate.user?.data ?? {}
    hydrate.user = { ...hydrate.user, data: userData }

    hydrate.app.client = true
    hydrate.app.server = false
    hydrate.app.bootstrapPhase = null
    // Keep the SSR viewport through hydrateRoot. Overwriting with getViewport()
    // here caused React #418 once Member Home / detail heroes painted real rows
    // that branch on width. Real viewport is applied after bootstrap via
    // initViewport → setAppViewport in app watchers.
    hydrate.app.bootstrapComplete = false
    hydrate.app.initializationStartTime = startTime

    /**
     * renderApp / renderHeader / renderFooter are deliberately left alone. They
     * are decided once during SSR (the route tree renders for members and
     * anonymous visitors alike; only a webview hides the header and footer), and
     * re-deriving them here from the presence of auth would make the first
     * client render disagree with the server markup.
     */

    if (!rawAuth) {
      const uuid = await getAnonymousUuid()
      userData.anonymousUuid = uuid
    }

    /**
     * Hydrate the gaia plan selection from session storage if it exists
     * this gives persistence of the plan selection across page reloads
     * which is important for the gaia checkout flow
     */
    const gaiaPlanSelection = getSessionStorage(SESSION_STORAGE_GAIA_PLAN_SELECTION)
    if (gaiaPlanSelection) {
      hydrate.plans = { ...hydrate.plans, selection: gaiaPlanSelection }
    }

    const gaiaCheckoutDiscountCode = getSessionStorage(SESSION_STORAGE_GAIA_CHECKOUT_DISCOUNT_CODE)
    if (gaiaCheckoutDiscountCode) {
      hydrate.checkout = { ...hydrate.checkout, discountCode: gaiaCheckoutDiscountCode }
    }

    // Keep the SSR user.requestCountry through hydrateRoot (unset →
    // AccountCreateForm falls back to appCountry US). Overwriting here would
    // change the first client render. SSR seeds requestCountry from
    // cloudfront-viewer-country; watchRequestCountryBootstrap is only the
    // fallback when that header was absent.

    /**
     * The location information for the resolver needs to be rebuilt here due to caching issues,
     * primarily with query parameters. If a page is cached by the server, then the server side
     * hydration will contain the incorrect query parameters. This will now use the location
     * parameters provided by the client on app bootstrap.
     */
    hydrate.resolver.location = historyLocation
    hydrate.resolver.path = _get(historyLocation, 'pathname', '')
    hydrate.resolver.query = _get(historyLocation, 'query', {})
    hydrate.resolver.processing = false

    // Seed Apollo with SSR GET_CONTENT so cache-first skips remount fetches.
    restoreApolloContentSeeds(apolloClient, hydrate)

    // configureStore is still JavaScript, so the store shape is asserted here.
    const store = await createStore({
      initialState, logLevel, hydrate, history,
    }) as AppStore

    history.listen((location) => {
      if (!isRecord(location)) {
        return
      }
      attachResolverHistoryListener({ store, location })
    })

    // SSR onAllReady already paints the lazy page. Client React.lazy still
    // suspends one tick after a cached import(), which mismatches that HTML
    // (React #418). Await preload so hydrateRoot commits the real page.
    try {
      await preloadHydrateRouteModules({
        pathname: String(_get(historyLocation, 'pathname', '/')),
        query: _get(historyLocation, 'query'),
      })
    } catch (err) {
      logBrowserError(err, 'preloadHydrateRouteModules')
    }

    try {
      const appElement = document.getElementById('app')
      if (!appElement) {
        throw new Error('Browser run could not find the #app mount node')
      }
      const component = renderApp({ store, history })
      // Starts the fixed, PII-free readiness window immediately before React
      // hydration; subtree effects record initial commit only to bound volume.
      markMilestone({ milestone: 'bootstrap' })
      hydrateRoot(appElement, component)
    } catch (err) {
      logBrowserError(err, 'app')
    }
  } catch (e) {
    logger.error(e)
  }
}

export default run
